import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function PaymentSuccess() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const { refreshUser } = useAuth();
    const [status, setStatus] = useState("pending"); // pending | paid | timeout | missing

    useEffect(() => {
        if (!sessionId) { setStatus("missing"); return; }
        let alive = true;
        let tries = 0;
        const poll = async () => {
            try {
                const r = await api.get(`/payments/status/${sessionId}`);
                if (!alive) return;
                if (r.data.payment_status === "paid") {
                    setStatus("paid");
                    refreshUser?.();
                    return;
                }
            } catch (err) {
                if (err.response?.status === 404) { if (alive) setStatus("timeout"); return; }
            }
            tries += 1;
            if (alive && tries < 15) setTimeout(poll, 2000);
            else if (alive) setStatus("timeout");
        };
        poll();
        return () => { alive = false; };
        // eslint-disable-next-line
    }, [sessionId]);

    return (
        <div className="min-h-screen pt-[68px] relative overflow-hidden" data-testid="payment-success-page">
            <div className="absolute inset-0 -z-10">
                <div className="absolute inset-0 grid-lines opacity-30" />
                <div className="absolute inset-0 bg-gradient-to-br from-[#0A0A0A] via-void to-[#050505]" />
            </div>
            <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-32">
                <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                    className="max-w-2xl border border-white/10 clip-frame bg-[#0A0A0A]/80 backdrop-blur-xl p-8 md:p-12">
                    <div className="overline mb-6">[ Payment · State Pass ]</div>
                    {status === "paid" && (
                        <>
                            <div className="w-14 h-14 border border-volt bg-volt/10 flex items-center justify-center font-display font-black text-2xl text-volt mb-6" data-testid="payment-confirmed-icon">✓</div>
                            <h1 className="font-display font-black tracking-tighter text-4xl md:text-6xl leading-[0.9]">
                                Pass <span className="text-volt">active.</span>
                            </h1>
                            <p className="mt-6 text-secondary text-base leading-relaxed">
                                Your State Pass is live. Every qualifier in your home state — Warzone and MW4 Multiplayer — plus all free cash cups are now unlocked on your account.
                            </p>
                            <div className="mt-10 flex flex-wrap gap-4">
                                <Link to="/tournaments" className="btn-primary" data-testid="payment-success-brackets-cta">Enter your state's brackets →</Link>
                                <Link to="/teams" className="btn-ghost" data-testid="payment-success-teams-cta">Manage your team</Link>
                            </div>
                        </>
                    )}
                    {status === "pending" && (
                        <>
                            <h1 className="font-display font-black tracking-tighter text-4xl md:text-6xl leading-[0.9]">
                                Confirming <span className="text-outline-strong">payment…</span>
                            </h1>
                            <p className="mt-6 text-secondary text-base" data-testid="payment-confirming-text">Talking to Stripe. This takes a few seconds — keep this tab open.</p>
                            <div className="mt-8 h-1 bg-white/10 overflow-hidden"><div className="h-full w-1/3 bg-volt animate-pulse" /></div>
                        </>
                    )}
                    {(status === "timeout" || status === "missing") && (
                        <>
                            <h1 className="font-display font-black tracking-tighter text-4xl md:text-6xl leading-[0.9]">
                                Still <span className="text-outline-strong">confirming.</span>
                            </h1>
                            <p className="mt-6 text-secondary text-base leading-relaxed" data-testid="payment-timeout-text">
                                Payment received — confirmation is taking longer than usual. Your pass will activate automatically. Check your brackets in a minute.
                            </p>
                            <div className="mt-10"><Link to="/tournaments" className="btn-primary" data-testid="payment-timeout-brackets-cta">Back to tournaments →</Link></div>
                        </>
                    )}
                </motion.div>
            </div>
        </div>
    );
}
